"use client";

import { useState, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { safeRedirectPath } from "@/lib/safe-redirect";
import { forgotPasswordHref, onboardingHref, useEditionSlug } from "@/lib/onboarding/edition-link";

export function LoginForm({
  brandName,
  headline,
  subhead,
  showSignup,
}: {
  brandName: string;
  headline: string;
  subhead: string;
  /* The anonymous funnel is closed until release (US-signup-closed/AC-1);
     only a browser holding the preview cookie still sees the way in. */
  showSignup: boolean;
}) {
  const router = useRouter();
  const params = useSearchParams();
  // The way back OUT of sign-in. "New to <brand>? Create an account" pointed at
  // "/onboarding" flat, so the one link that closes the loop handed a reseller's
  // visitor to the house funnel — the sign-in defect in the return direction.
  // `useEditionSlug` reads the live URL, which is where `loginHref` now puts the
  // edition, and it is the same hook the funnel's own links already use.
  const edition = useEditionSlug();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    const res = await signIn("credentials", { email, password, redirect: false });
    setPending(false);
    if (res?.error) {
      toast.error("Invalid email or password");
      return;
    }
    router.push(safeRedirectPath(params.get("callbackUrl"), "/dashboard"));
    router.refresh();
  }

  return (
    <div className="ob-stagger">
      <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.02em]">{headline}</h1>
      <p className="mt-2 text-[15px] text-muted-foreground">{subhead}</p>
      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <div>
          <label className="ob-label" htmlFor="email">Email</label>
          <input className="ob-input"
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={pending}
          />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label className="ob-label" htmlFor="password">Password</label>
            <Link href={forgotPasswordHref(edition)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
              Forgot password?
            </Link>
          </div>
          <input className="ob-input"
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={pending}
          />
        </div>
        <button type="submit" disabled={pending} className="ob-cta mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium">
          {pending ? <Spinner className="size-4" /> : null}
          Sign in
        </button>
      </form>
      {showSignup ? (
        <p className="mt-6 text-sm text-muted-foreground">
          New to {brandName}?{" "}
          <Link href={onboardingHref(edition)} className="font-medium text-brand hover:underline">
            Create an account
          </Link>
        </p>
      ) : null}
    </div>
  );
}
